import React from 'react';
import {
  Modal,
  Button,
  Descriptions,
  Tag,
  Tooltip,
  Divider,
  Table,
  Space,
  Image,
  Typography,
} from 'antd';
import {
  UserOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import { AttachmentItem } from '../CycleLogAttachments';
import {
  getLogTypeLabel,
  formatDateTime,
  getResolvedElementInfo,
  renderInputsSummary,
  renderOutputsSummary,
  renderTotalCol,
  getTagRecord,
} from './cycleDetailHelpers';

const { Text } = Typography;

export interface CycleDetailLogDetailModalProps {
  visible: boolean;
  onClose: () => void;
  selectedLog: any;
  cycle: any;
  itemsMap: Map<string, any>;
  isMobile?: boolean;
}

export const CycleDetailLogDetailModal: React.FC<CycleDetailLogDetailModalProps> = ({
  visible,
  onClose,
  selectedLog,
  cycle,
  itemsMap,
  isMobile = false,
}) => {
  if (!selectedLog) return null;

  const resolvedMovementUid =
    selectedLog.storage_movement_uid ||
    selectedLog.production_cycle_log_inputs?.find(
      (i: any) => i.storage_movement_uid
    )?.storage_movement_uid ||
    selectedLog.production_cycle_log_outputs?.find(
      (o: any) => o.storage_movement_uid
    )?.storage_movement_uid;

  const resolvedContactName =
    selectedLog.contacts?.name ||
    selectedLog.contact_name ||
    selectedLog.production_cycle_log_inputs?.find(
      (i: any) => i.contacts?.name
    )?.contacts?.name ||
    selectedLog.production_cycle_log_inputs?.[0]?.contact_name;

  const resolvedStorehouseName =
    selectedLog.storehouse_name ||
    selectedLog.storehouses?.name ||
    selectedLog.production_cycle_log_outputs?.[0]?.storehouses?.name;

  const isHeaderContactType =
    selectedLog.log_type === 'SUPPLY_PURCHASE' ||
    selectedLog.log_type === 'SERVICE_EXPENSE' ||
    selectedLog.log_type === 'LABOR_LOG';

  return (
    <Modal
      title={`Detalle de Evento: ${
        selectedLog ? getLogTypeLabel(selectedLog.log_type) : ''
      }`}
      open={visible}
      onCancel={onClose}
      maskClosable={false}
      footer={[
        <Button key="close" onClick={onClose}>
          Cerrar
        </Button>,
      ]}
      width={isMobile ? '100%' : 700}
      style={
        isMobile
          ? { top: 10, maxWidth: '100vw', padding: '0 4px' }
          : undefined
      }
    >
      <div>
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item
            label="Fecha y Hora"
            span={selectedLog.production_cycle_stages?.name ? 1 : 2}
          >
            {formatDateTime(selectedLog.log_date)}
          </Descriptions.Item>
          {selectedLog.production_cycle_stages?.name && (
            <Descriptions.Item label="Etapa" span={1}>
              {selectedLog.production_cycle_stages.name}
            </Descriptions.Item>
          )}
          {resolvedContactName && (
            <Descriptions.Item
              label={
                selectedLog.log_type === 'LABOR_LOG'
                  ? 'Trabajador / Jornalero'
                  : selectedLog.log_type === 'SUPPLY_PURCHASE' ||
                    selectedLog.log_type === 'SERVICE_EXPENSE'
                  ? 'Proveedor'
                  : 'Responsable'
              }
              span={resolvedMovementUid ? 1 : 2}
            >
              <Tag
                color={
                  selectedLog.log_type === 'LABOR_LOG' ? 'purple' : 'blue'
                }
                icon={<UserOutlined />}
                style={{ fontSize: 12, padding: '2px 8px' }}
              >
                {resolvedContactName}
              </Tag>
            </Descriptions.Item>
          )}
          {resolvedMovementUid && (
            <Descriptions.Item
              label="Comprobante / Movimiento"
              span={resolvedContactName ? 1 : 2}
            >
              <Tooltip
                title={`Comprobante / Movimiento en Almacén: ${resolvedMovementUid}`}
              >
                <Tag
                  color="cyan"
                  style={{
                    fontWeight: 600,
                    fontSize: 12,
                    padding: '2px 8px',
                  }}
                >
                  Doc:{' '}
                  {resolvedMovementUid.length > 10
                    ? resolvedMovementUid.slice(-6)
                    : resolvedMovementUid}
                </Tag>
              </Tooltip>
            </Descriptions.Item>
          )}
          {resolvedStorehouseName && (
            <Descriptions.Item label="Almacén / Bodega" span={2}>
              <Tag color="orange">{resolvedStorehouseName}</Tag>
            </Descriptions.Item>
          )}
          {selectedLog.quantity_change !== null &&
            selectedLog.quantity_change !== undefined && (
              <Descriptions.Item label="Cambio en Capacidad" span={2}>
                <Text
                  type={
                    selectedLog.quantity_change > 0 ? 'success' : 'danger'
                  }
                  strong
                >
                  {selectedLog.quantity_change > 0 ? '+' : ''}
                  {selectedLog.quantity_change} {cycle?.unit_of_measure}
                </Text>
              </Descriptions.Item>
            )}
        </Descriptions>

        <Divider orientation="left">Notas / Observaciones</Divider>
        <div
          style={{
            background: '#f9f9f9',
            padding: '12px',
            borderRadius: '4px',
            fontStyle: 'italic',
          }}
        >
          {selectedLog.notes || 'Sin observaciones registradas.'}
        </div>

        {selectedLog.metrics &&
          Object.keys(selectedLog.metrics).length > 0 && (
            <>
              <Divider orientation="left">Métricas Registradas</Divider>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {Object.entries(selectedLog.metrics).map(([k, v]) => (
                  <Tag color="blue" key={k} style={{ padding: '4px 8px' }}>
                    <b>{k}:</b> {String(v)}
                  </Tag>
                ))}
              </div>
            </>
          )}

        {selectedLog.production_cycle_log_inputs &&
          selectedLog.production_cycle_log_inputs.length > 0 && (
            <>
              <Divider orientation="left">
                {selectedLog.log_type === 'SUPPLY_PURCHASE'
                  ? '🛒 Insumos Comprados (Entrada a Inventario)'
                  : selectedLog.log_type === 'LABOR_LOG'
                  ? '🧑‍🌾 Jornales y Labores Registrados'
                  : selectedLog.log_type === 'SERVICE_EXPENSE'
                  ? '🚚 Servicios y Gastos Externos'
                  : 'Insumos Aplicados'}
              </Divider>
              <Table
                size="small"
                pagination={false}
                dataSource={selectedLog.production_cycle_log_inputs}
                rowKey="id"
                scroll={{ x: 'max-content' }}
                summary={renderInputsSummary}
                columns={[
                  {
                    title:
                      selectedLog.log_type === 'SUPPLY_PURCHASE'
                        ? 'Insumo Comprado'
                        : selectedLog.log_type === 'LABOR_LOG'
                        ? 'Labor / Actividad'
                        : selectedLog.log_type === 'SERVICE_EXPENSE'
                        ? 'Servicio / Gasto'
                        : 'Item / Insumo',
                    key: 'name',
                    render: (_, record: any) => {
                      const info = getResolvedElementInfo(record, itemsMap);
                      const isServ = info.isService;
                      const code = info.code;
                      const logType = selectedLog.log_type;
                      let tagLabel = 'Insumo';
                      let tagColor = 'green';
                      if (logType === 'LABOR_LOG') {
                        tagLabel = 'Labor';
                        tagColor = 'magenta';
                      } else if (logType === 'SERVICE_EXPENSE') {
                        tagLabel = 'Servicio/Gasto';
                        tagColor = 'geekblue';
                      } else if (logType === 'SUPPLY_PURCHASE') {
                        tagLabel = 'Compra';
                        tagColor = 'orange';
                      } else if (isServ) {
                        tagLabel = 'Servicio';
                        tagColor = 'geekblue';
                      }
                      return (
                        <div>
                          <div
                            style={{
                              fontWeight: 600,
                              color: '#1f1f1f',
                            }}
                          >
                            {info.name}
                          </div>
                          <Space size={4} style={{ marginTop: 2 }}>
                            <Tag
                              color={tagColor}
                              style={{
                                fontSize: 10,
                                lineHeight: '16px',
                                padding: '0 4px',
                                margin: 0,
                              }}
                            >
                              {tagLabel}
                            </Tag>
                            {code && (
                              <Tag
                                bordered={false}
                                style={{
                                  fontSize: 10,
                                  lineHeight: '16px',
                                  padding: '0 4px',
                                  margin: 0,
                                  background: '#f5f5f5',
                                }}
                              >
                                Ref: {code}
                              </Tag>
                            )}
                          </Space>
                        </div>
                      );
                    },
                  },
                  {
                    title: 'Cant.',
                    key: 'qty',
                    align: 'right',
                    render: (_, record: any) => {
                      const info = getResolvedElementInfo(record, itemsMap);
                      return (
                        <span>
                          <strong>{record.quantity}</strong>{' '}
                          {info.measureUnit ? (
                            <small style={{ color: '#888' }}>
                              {info.measureUnit}
                            </small>
                          ) : null}
                        </span>
                      );
                    },
                  },
                  {
                    title:
                      selectedLog.log_type === 'SUPPLY_PURCHASE'
                        ? 'Precio Compra U.'
                        : selectedLog.log_type === 'LABOR_LOG'
                        ? 'Tarifa Jornal'
                        : selectedLog.log_type === 'SERVICE_EXPENSE'
                        ? 'Valor U.'
                        : 'Costo U.',
                    dataIndex: 'unit_price',
                    key: 'price',
                    align: 'right',
                    render: (val) =>
                      val
                        ? `$ ${new Intl.NumberFormat('es-CO').format(val)}`
                        : '-',
                  },
                  {
                    title: 'Total',
                    key: 'total',
                    align: 'right',
                    render: renderTotalCol,
                  },
                  ...(!isHeaderContactType &&
                  selectedLog.production_cycle_log_inputs?.some(
                    (i: any) => i.contact_id || i.contacts?.name
                  )
                    ? [
                        {
                          title: 'Responsable',
                          key: 'contact',
                          render: (record: any) =>
                            getTagRecord(record, selectedLog),
                        },
                      ]
                    : []),
                ]}
              />
            </>
          )}

        {selectedLog.production_cycle_log_outputs &&
          selectedLog.production_cycle_log_outputs.length > 0 && (
            <>
              <Divider orientation="left">Producción Obtenida</Divider>
              <Table
                size="small"
                pagination={false}
                dataSource={selectedLog.production_cycle_log_outputs}
                rowKey="id"
                scroll={{ x: 'max-content' }}
                summary={renderOutputsSummary}
                columns={[
                  {
                    title: 'Producto Cosechado / Producido',
                    key: 'name',
                    render: (_, record: any) => {
                      const el = record.storage_elements || {};
                      const code = el.custom_id || el.sku;
                      return (
                        <div>
                          <div
                            style={{
                              fontWeight: 600,
                              color: '#1f1f1f',
                            }}
                          >
                            {el.name || 'Sin nombre'}
                          </div>
                          <Space size={4} style={{ marginTop: 2 }}>
                            <Tag
                              color="cyan"
                              style={{
                                fontSize: 10,
                                lineHeight: '16px',
                                padding: '0 4px',
                                margin: 0,
                              }}
                            >
                              Producto
                            </Tag>
                            {code && (
                              <Tag
                                bordered={false}
                                style={{
                                  fontSize: 10,
                                  lineHeight: '16px',
                                  padding: '0 4px',
                                  margin: 0,
                                  background: '#f5f5f5',
                                }}
                              >
                                Ref: {code}
                              </Tag>
                            )}
                          </Space>
                        </div>
                      );
                    },
                  },
                  {
                    title: 'Cant.',
                    key: 'qty',
                    align: 'right',
                    render: (_, record: any) => (
                      <span>
                        <strong>{record.quantity}</strong>{' '}
                        {record.storage_elements?.measure_unit ? (
                          <small style={{ color: '#888' }}>
                            {record.storage_elements.measure_unit}
                          </small>
                        ) : null}
                      </span>
                    ),
                  },
                  {
                    title: 'Valor Est.',
                    dataIndex: 'unit_price',
                    key: 'price',
                    render: (val) =>
                      val
                        ? `$ ${new Intl.NumberFormat('es-CO').format(val)}`
                        : '-',
                  },
                  {
                    title: 'Total Est.',
                    key: 'total',
                    align: 'right',
                    render: renderTotalCol,
                  },
                ]}
              />
            </>
          )}

        {/* Adjuntos del evento en el modal de detalle */}
        {selectedLog.production_cycle_log_attachments &&
          selectedLog.production_cycle_log_attachments.length > 0 && (
            <>
              <Divider orientation="left">Adjuntos / Soportes</Divider>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 12,
                  padding: '0 12px',
                }}
              >
                {(
                  selectedLog.production_cycle_log_attachments as AttachmentItem[]
                ).map((att) => (
                  <Tooltip key={att.id} title={att.file_name}>
                    <div style={{ textAlign: 'center' }}>
                      {att.file_type === 'image' ? (
                        <Image
                          src={att.file_url}
                          width={80}
                          height={80}
                          style={{
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #d9d9d9',
                            cursor: 'pointer',
                          }}
                          preview={{ src: att.file_url }}
                        />
                      ) : (
                        <a
                          href={att.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            width: 80,
                            height: 80,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #ffccc7',
                            borderRadius: 8,
                            background: '#fff2f0',
                            cursor: 'pointer',
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          <FilePdfOutlined
                            style={{ color: '#f5222d', fontSize: 32 }}
                          />
                          <span
                            style={{
                              fontSize: 11,
                              color: '#888',
                              marginTop: 4,
                            }}
                          >
                            Ver PDF
                          </span>
                        </a>
                      )}
                      <div
                        style={{
                          fontSize: 10,
                          color: '#888',
                          marginTop: 4,
                          maxWidth: 80,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {att.file_name}
                      </div>
                    </div>
                  </Tooltip>
                ))}
              </div>
            </>
          )}
      </div>
    </Modal>
  );
};
